/**
 * Countdown CTA
 * Daily reset countdown timer for Shopify sections.
 *
 * Usage:
 *   <div data-countdown-cta data-cutoff-hour="16">
 *     <span data-countdown-unit="hours">00</span>
 *     <span data-countdown-unit="minutes">00</span>
 *     <span data-countdown-unit="seconds">00</span>
 *   </div>
 */

(function () {
  'use strict';

  const SELECTOR = '[data-countdown-cta]';
  const DEFAULT_CUTOFF_HOUR = 16; // 4 PM
  const DEFAULT_CUTOFF_MINUTE = 0;

  /**
   * Pad number to 2 digits
   */
  function pad(n) {
    return String(n).padStart(2, '0');
  }

  /**
   * Get today's cutoff time in browser's local timezone.
   * If cutoff already passed, return tomorrow's cutoff.
   */
  function getTargetTime(cutoffHour, cutoffMinute) {
    const now = new Date();
    const target = new Date();
    target.setHours(cutoffHour, cutoffMinute, 0, 0);

    // Cutoff already passed today → target tomorrow
    if (now >= target) {
      target.setDate(target.getDate() + 1);
    }
    return target;
  }

  /**
   * Initialize a single countdown element
   */
  function initCountdown(el) {
    const timerEl = el.querySelector('[data-countdown-timer]') || el;
    const hoursEl = timerEl.querySelector('[data-countdown-unit="hours"]');
    const minutesEl = timerEl.querySelector('[data-countdown-unit="minutes"]');
    const secondsEl = timerEl.querySelector('[data-countdown-unit="seconds"]');

    // Nothing to update — bail out
    if (!hoursEl && !minutesEl && !secondsEl) return;

    const cutoffHour = parseInt(
      el.dataset.cutoffHour || DEFAULT_CUTOFF_HOUR,
      10
    );
    const cutoffMinute = parseInt(
      el.dataset.cutoffMinute || DEFAULT_CUTOFF_MINUTE,
      10
    );

    let intervalId = null;

    function tick() {
      const now = new Date();
      const target = getTargetTime(cutoffHour, cutoffMinute);
      const diffSeconds = Math.max(
        0,
        Math.floor((target - now) / 1000)
      );

      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;

      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    }

    function start() {
      if (intervalId) return;
      tick();
      intervalId = setInterval(tick, 1000);
    }

    function stop() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    // Pause when tab hidden (saves CPU)
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    // Pause when element scrolled out of view (IntersectionObserver)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              start();
            } else {
              stop();
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(el);
    } else {
      // Fallback for old browsers
      start();
    }
  }

  /**
   * Initialize all countdowns on the page.
   * Re-runnable (safe for Shopify section reload).
   */
  function init() {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (el.dataset.countdownInitialized === 'true') return;
      el.dataset.countdownInitialized = 'true';
      initCountdown(el);
    });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify theme editor / section reload
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);

  // Expose for manual trigger if needed
  window.CountdownCTA = { init: init };
})();